import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { Button, Spinner } from "@/components/reusable";
import { downloadMappedFileXlsx } from "@/services/api";
import { cn } from "@/lib/utils";

interface DownloadMappedFileButtonProps {
  fileId: string;
  fallbackFileName: string;
  /** When set, parent owns the download (e.g. pipeline action lock). Otherwise calls downloadMappedFileXlsx. */
  onDownload?: (fileId: string) => void | Promise<void>;
  /** External busy flag from a parent action lock (pipeline). */
  pending?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "ghost" | "danger" | "link";
  className?: string;
  "data-testid"?: string;
  /** Optional label override; defaults to i18n history.downloadMapped. */
  label?: ReactNode;
  preparingLabel?: ReactNode;
  onSuccess?: (fileId: string) => void;
  onError?: (error: Error, fileId: string) => void;
}

/**
 * Shared "Download mapped file" control for History preview and Pipeline published cards.
 * History uses the default API path; Pipeline passes onDownload to keep beginPipelineAction locking.
 */
export function DownloadMappedFileButton({
  fileId,
  fallbackFileName,
  onDownload,
  pending = false,
  disabled = false,
  size = "sm",
  variant = "secondary",
  className,
  "data-testid": dataTestId,
  label,
  preparingLabel,
  onSuccess,
  onError,
}: DownloadMappedFileButtonProps) {
  const { t } = useTranslation();
  const [internalBusy, setInternalBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const busy = pending || internalBusy;

  const handleClick = useCallback(async () => {
    if (busy || disabled) return;
    setErrorMessage(null);

    if (onDownload) {
      try {
        await onDownload(fileId);
        onSuccess?.(fileId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setErrorMessage(error.message || t("history.downloadMappedError"));
        onError?.(error, fileId);
      }
      return;
    }

    setInternalBusy(true);
    try {
      await downloadMappedFileXlsx(fileId, fallbackFileName);
      onSuccess?.(fileId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setErrorMessage(error.message || t("history.downloadMappedError"));
      onError?.(error, fileId);
    } finally {
      setInternalBusy(false);
    }
  }, [busy, disabled, onDownload, fileId, fallbackFileName, onSuccess, onError, t]);

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        type="button"
        size={size}
        variant={variant}
        className={cn(className)}
        onClick={() => void handleClick()}
        disabled={disabled || busy}
        aria-busy={busy}
        data-testid={dataTestId}
      >
        {busy ? (
          <span className="flex items-center gap-1.5">
            <Spinner size="sm" />
            {preparingLabel ?? t("history.downloadMappedPreparing")}
          </span>
        ) : (
          <span className="inline-flex min-w-0 items-center justify-center gap-1">
            <Download className="h-3 w-3 shrink-0" strokeWidth={1.7} aria-hidden />
            <span className="truncate">{label ?? t("history.downloadMapped")}</span>
          </span>
        )}
      </Button>
      {errorMessage ? (
        <p className="text-xs text-[var(--color-error)]" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
